import moment from "moment";
import { useContext, useEffect, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation, withTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChatShowContext, LoaderContext, SidebarContext } from "../../../App";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { LS_COMPANY_INFO_KEY, LS_UI_ELEMENTS_CONFIG_KEY, ReportPageType, ReportSection, ReportStatus, currentYear } from '../../../core/constants/common';
import { useCallbackPrompt } from "../../../core/hooks/useCallbackPrompt";
import GeneralDisclosureService from "../../../core/service/GeneralDisclosureService";
import ReportService from '../../../core/service/ReportService';
import StandardService from "../../../core/service/StandardService";
import BRSRReportGenerator from '../../../core/utilities/BRSRReportGenerator';
import GHGReportingUtils, { pagetableTYPE_5Function, pagetableTYPE_6Function, pagetableTYPE_7Function } from "../../../core/utilities/GHGReportingUtils";
import { shouldPreserveSavedGHGTable } from "../../../core/utilities/ReportingContentUtils";
import ReportingUtils from "../../../core/utilities/ReportingUtils";
import { buildReportingListUrl } from "../../../core/utilities/reportingListUrl";
import { ensureLanguageLoaded, normalizeLanguageCode } from "../../../core/utilities/TranslationHelpers";
import Utils from "../../../core/utilities/Utils";
import BRSRReportingCard from './BRSR/BRSRReportingCard';
import BRSRReportingSidebar from './BRSR/BRSRReportingSidebar';
import ESRSReportingCard from './ESRSReportingCard';
import ESRSReportingSidebar from './ESRSReportingSidebar';
import './Reporting.scss';
import ReportingCard from './ReportingCard';
import ReportingSidebar from './ReportingSidebar';
import ReportingStandardData from "./ReportingStandardData";


const ReportingContainer = ({ t }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lsDat = JSON.parse(localStorage.getItem("authority"));
  const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY));
  const companyId = lsDat.profile.company.id;
  const regulationName = searchParams.get('regulation') ?? null;
  const regulationId = searchParams.get('regulationId') ?? 0;
  const regulationFrequency = searchParams.get('frequency') ?? '';
  const yearParam = searchParams.get('year');
  const yearFilter = yearParam ? +yearParam : currentYear;
  const stdType = searchParams.get('stdType') ?? '';
  const mode = searchParams.get('mode') ?? 'EDIT';
  const isCoDraftMode = searchParams.get('coDraft') == 'true';
  const coDraftJobId = searchParams.get('jobId');
  const activeTab = searchParams.get('activeTab') ?? '';
  const listPage = searchParams.get('listPage') ?? '';
  const listSize = searchParams.get('size') ?? '';
  const reportPeriodStart = searchParams.get('periodStart') ?? '';
  const reportPeriodEnd = searchParams.get('periodEnd') ?? '';
  const reportEntities = Utils.convertNumberStringToArray(searchParams.get('entities') ?? '');
  const source = searchParams.get('source') ?? 'reporting-list';
  const metricDataPageDefaultHTML = `
    <div style="font-family: Arial, sans-serif;font-weight:400;">
      <div>
        <h4>${regulationName} Index</h4>
        <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
      </div>
    </div>
  `;
  const sustIndexPageDefaultHTML = `
    <div style="font-family: Arial, sans-serif;font-weight:400;">
      <div>
        <h4>Content Index</h4>
        <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
      </div>
    </div>
  `;

  const [reportId, setReportId] = useState(searchParams.get('id') ?? null);
  const [report, setReport] = useState({});
  const [renderedOn, setRenderedOn] = useState(0);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [isContentEdited, setIsContentEdited] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(isContentEdited);
  const [pausedPageIndexToNavigate, setPausedPageIndexToNavigate] = useState({
    page: null,
    section: null
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const reportCardGetContentRef = useRef(null);
  const { setSidebarDisplay } = useContext(SidebarContext);
  const { setLoaderDisplay } = useContext(LoaderContext);
  const { setShowChat } = useContext(ChatShowContext);

  // Hide global ESG AI copilot button when on reporting page (page has its own embedded copilot)
  useEffect(() => {
    setShowChat('Reporting');
    return () => setShowChat('Global');
  }, [setShowChat]);
  const uiElementsConfigJson = localStorage.getItem(LS_UI_ELEMENTS_CONFIG_KEY);
  const uiElementsConfig = uiElementsConfigJson ? JSON.parse(uiElementsConfigJson) : null;
  const [jobId, setJobId] = useState(coDraftJobId ? +coDraftJobId : 0);
  const [Loader, setLoader] = useState(false);

  useEffect(() => {
    setSidebarDisplay(false);
    if (reportId > 0) {
      if (stdType === 'BRSR' && mode === 'CREATE') {
        generateBRSRReportStructureFromTemplate();
      } else {
        fetchReport();
      }
    } else {
      // Generate static content
      const generateStaticContent = async () => {
        try {
          // Ensure language resources are loaded before generating report sections
          await ensureLanguageLoaded(i18n, i18n.language);

          const reportClone = {
            name: `${regulationName} Report - ${moment().format('DD.MM.YYYY')}`,
            status: ReportStatus.DRAFT,
            sections: [],
            regulationId: regulationId,
            companyId: companyId
          };
          const language = i18n.language;
          reportClone.sections.push(ReportingUtils.getCoverSection(reportClone, companyId, t, language, i18n));
          if (stdType == 'GHG') {
            reportClone.sections.push(GHGReportingUtils.getTableOfContentsSection(t, i18n.language, i18n));
          } else {
            reportClone.sections.push(ReportingUtils.getTableOfContentsSection(t, language, i18n));
          }
          reportClone.sections.push(ReportingUtils.getLeadershipSection(t, language, i18n));
          reportClone.sections.push(ReportingUtils.getAboutReportSection(t, language, i18n));
          reportClone.sections.push(ReportingUtils.getAboutCompanySection(t, language, i18n));
          reportClone.sections.push(ReportingUtils.getOurESGApproachSection(t, language, i18n));
          if (stdType == 'ESRS') {
            reportClone.sections.push(ReportingUtils.getESRSEnvironmentSection([], [], [], [], [], language, t, i18n));
            reportClone.sections.push(ReportingUtils.getESRSSocialSection([], [], [], [], language, t, i18n));
            reportClone.sections.push(ReportingUtils.getESRSGovernanceSection([], language, t, i18n));
          } else if (stdType == 'GHG') {
            const ghgDecimals = { decimalPlaces: companyInfo?.decimalPlaces };
            // New GHG Report Structure
            reportClone.sections.push(GHGReportingUtils.getExecutiveSummarySection([], i18n.language, t, i18n, ghgDecimals));
            reportClone.sections.push(GHGReportingUtils.getAboutReportSection([], i18n.language, t, i18n, ghgDecimals));
            reportClone.sections.push(GHGReportingUtils.getOrganizationalProfileSection([], i18n.language, t, i18n, ghgDecimals));
            reportClone.sections.push(GHGReportingUtils.getBaseYearMethodologySection([], i18n.language, t, i18n, ghgDecimals));
            reportClone.sections.push(GHGReportingUtils.getGHGEmissionsInventorySection([], i18n.language, t, i18n, ghgDecimals));
            reportClone.sections.push(GHGReportingUtils.getEnergyUseEmissionSourcesSection([], i18n.language, t, i18n, ghgDecimals));
            reportClone.sections.push(GHGReportingUtils.getGHGReductionStrategySection([], i18n.language, t, i18n, ghgDecimals));
            reportClone.sections.push(GHGReportingUtils.getVerificationAssuranceSection([], i18n.language, t, i18n, ghgDecimals));
            reportClone.sections.push(GHGReportingUtils.getInsightsSection([], i18n.language, t, i18n, ghgDecimals));
            reportClone.sections.push(GHGReportingUtils.getAnnexuresSection([], i18n.language, t, i18n, ghgDecimals));
          } else {
            reportClone.sections.push(ReportingUtils.getEnvironmentSection([], language, i18n));
            reportClone.sections.push(ReportingUtils.getSocialSection([], language, i18n));
            reportClone.sections.push(ReportingUtils.getGovernanceSection([], language, i18n));
            reportClone.sections.push(ReportingUtils.getESGDisclosureSection(language, i18n));
          }
          if (stdType == 'GHG') {
            reportClone.sections.push(GHGReportingUtils.getBackCover(companyId, yearFilter, i18n.language.toUpperCase(), t));
          } else {
            reportClone.sections.push(ReportingUtils.getBackCover(uiElementsConfig?.texts?.partnerLogoURL ?? null, language, i18n));
          }
          refreshSectionAndPageIndices(reportClone);
          if (stdType === 'GRI') {
            ReportingUtils.normalizeGriContentsSectionName(reportClone, t('Contents.label'));
            ReportingUtils.applyGriTableOfContentsToReport(reportClone, t('TableofContents.label'));
          }
          setReport(reportClone);
          fetchMetricData(reportClone, `${yearFilter}-01-01`, false, reportPeriodStart, reportPeriodEnd);
          setJobId(Date.now());
        } catch (error) {
          console.error('Error generating static content:', error);
        }
      };

      generateStaticContent();
    }
  }, []);

  useEffect(() => {

    return () => {
      // On unmounting
      setSidebarDisplay(true);
    };
  }, []);

  const fetchReport = (id) => {
    setLoaderDisplay(true);
    if (stdType === 'GRI') {
      ReportService.getReport(id > 0 ? id : reportId).then(res => {
        setLoaderDisplay(false);
        if (res.status === 200) {
          const reportClone = res.data;
          reportClone.sections?.sort((a, b) => {
            return a.sortOrder - b.sortOrder;
          });
          if (isCoDraftMode) {
            reportClone.status = ReportStatus.DRAFT;
          }
          reportClone.sections?.forEach((section, index) => {
            section.index = index;
            section.type = ReportSection[section.type];
            section.pages?.sort((a, b) => {
              return a.sortOrder - b.sortOrder;
            });
            section.pages?.forEach((page, index) => {
              const decodedHTML = page.htmlContent ? decodeURIComponent(escape(window.atob(page.htmlContent))) : null;

              page.html = decodedHTML ? decodedHTML : null;
              page.json = page.jsonContent ? JSON.parse(page.jsonContent) : null;
              page.section = ReportSection[page.section];
              page.index = index;
              page.type = ReportPageType[page.type];
            });
          });

          ReportingUtils.normalizeGriContentsSectionName(reportClone, t('Contents.label'));
          ReportingUtils.applyGriTableOfContentsToReport(reportClone, t('TableofContents.label'));
          setReport(reportClone);
          setLoaderDisplay(false);
        }
      }).catch(error => {
        setLoaderDisplay(false);
        toast.error(error.title, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
    } else if (stdType === 'ESRS') {
      ReportService.getESRSReport(id > 0 ? id : reportId).then(res => {
        if (res.status === 200) {
          const reportClone = res.data;
          const page1Req = ReportService.getReportV2Template({ pageId: 1, type: 'ESRS', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities })
          const page2Req = ReportService.getReportV2Template({ pageId: 2, type: 'ESRS', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities })
          const page3Req = ReportService.getReportV2Template({ pageId: 3, type: 'ESRS', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities })
          const page4Req = ReportService.getReportV2Template({ pageId: 4, type: 'ESRS', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities })
          const page5Req = ReportService.getReportV2Template({ pageId: 5, type: 'ESRS', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities })
          const page6Req = ReportService.getReportV2Template({ pageId: 6, type: 'ESRS', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities })
          const page7Req = ReportService.getReportV2Template({ pageId: 7, type: 'ESRS', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities })
          const page8Req = ReportService.getReportV2Template({ pageId: 8, type: 'ESRS', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities })
          const page9Req = ReportService.getReportV2Template({ pageId: 9, type: 'ESRS', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities })
          const page10Req = ReportService.getReportV2Template({ pageId: 10, type: 'ESRS', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities })
          const page11Req = ReportService.getReportV2Template({ pageId: 11, type: 'ESRS', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities })
          Promise.all([
            page1Req,
            page2Req,
            page3Req,
            page4Req,
            page5Req,
            page6Req,
            page7Req,
            page8Req,
            page9Req,
            page10Req,
            page11Req
          ]).then(res => {
            const templateMapping = {
              D1: !!res[10].data.data ? res[10].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
              E1: !!res[0].data.data ? res[0].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
              E2: !!res[1].data.data ? res[1].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
              E3: !!res[2].data.data ? res[2].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
              E4: !!res[3].data.data ? res[3].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
              E5: !!res[4].data.data ? res[4].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
              S1: !!res[5].data.data ? res[5].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
              S2: !!res[6].data.data ? res[6].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
              S3: !!res[7].data.data ? res[7].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
              S4: !!res[8].data.data ? res[8].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
              G1: !!res[9].data.data ? res[9].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            };
            reportClone.sections?.sort((a, b) => {
              return a.sortOrder - b.sortOrder;
            });
            if (isCoDraftMode) {
              reportClone.status = ReportStatus.DRAFT;
            }
            reportClone.sections?.forEach((section, index) => {
              section.index = index;
              section.type = ReportSection[section.type];
              section.pages?.sort((a, b) => {
                return a.sortOrder - b.sortOrder;
              });
              section.pages?.forEach((page, index) => {
                var decodedHTML = page.htmlContent ? decodeURIComponent(escape(window.atob(page.htmlContent))) : null;
                var templateD = templateMapping[page.type] || [];
                if (templateD.length > 0) {
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(decodedHTML, "text/html");
                  for (let index = 0; index < templateD.length; index++) {
                    const element = templateD[index];
                    if (!element.key.trim().includes(" ") && element.keyValueType == "SINGLE_VALUE" && element.keyValues.length > 0) {
                      const date1 = new Date(element.keyValues[0].latestDate)
                      const date2 = new Date(reportClone.lastModifiedDate)
                      if (!!element.keyValues[0].latestDate && date2 < date1) {
                        const safeKey = CSS.escape(element.key);
                        const items = doc.querySelector(`.${safeKey}`);
                        if (!!items) {
                          const answer = element.keyValues[0]
                          items.innerText = !!answer.uom ? answer.uom == "no." ? parseInt(answer.value) : answer.value + ' ' + answer.uom : answer.value;
                          decodedHTML = doc.body.innerHTML;
                        }
                      }
                    }
                    if (!element.key.trim().includes(" ") && element.keyValueType == "TYPE_1_TABLE" || !element.key.trim().includes(" ") && element.keyValueType == "TYPE_2_TABLE" || !element.key.trim().includes(" ") && element.keyValueType == "TYPE_3_TABLE") {
                      for (let i = 0; i < element.keyValues.length; i++) {
                        const element1 = element.keyValues[i];
                        const date1 = new Date(element1.latestDate)
                        const date2 = new Date(reportClone.lastModifiedDate)
                        const tableKey = [];
                        if (element.keyValueType == "TYPE_3_TABLE") {
                          if (tableKey.indexOf(element1.key) == -1) {
                            const filterKeyValue = element.keyValues.filter(itm => itm.key == element1.key)
                            const safeKey = CSS.escape(element1.key);
                            const items = doc.querySelectorAll(`.${safeKey}`);
                            if (items.length > 0) {
                              items.forEach((item, index) => {
                                const answer = filterKeyValue[index]
                                const date3 = new Date(answer.latestDate)
                                if (!!answer.latestDate && date2 < date3) {
                                  item.innerText = !!answer.uom ? answer.uom == "no." ? parseInt(answer.value) : answer.value + ' ' + answer.uom : answer.value;
                                }
                              });
                              decodedHTML = doc.body.innerHTML;
                            }
                            tableKey.push(element1.key)
                          }
                        } else if (!!element1.latestDate && date2 < date1) {
                          const safeKey = CSS.escape(element1.key);
                          const items = doc.querySelectorAll(`.${safeKey}`);
                          if (items.length > 0) {
                            items.forEach((item, k) => {
                              item.innerText = !!element1.uom ? element1.uom == "no." ? parseInt(element1.value) : element1.value + ' ' + element1.uom : element1.value;
                            });
                            decodedHTML = doc.body.innerHTML;
                          }
                        }

                      }
                    }
                  }
                }
                page.html = decodedHTML;
                page.json = page.jsonContent ? JSON.parse(page.jsonContent) : null;
                page.section = ReportSection[page.section];
                page.index = index;
                page.type = ReportPageType[page.type];
              });
            });
            setReport(reportClone);
            setLoaderDisplay(false);
          }).catch(err => {
            toast.error("Error fetching template data", {
              position: toast.POSITION.TOP_RIGHT,
            });
            setLoaderDisplay(false);
          });
        }
      }).catch(error => {
        setLoaderDisplay(false);
        toast.error(error.title, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
    } else if (stdType === 'VSME') {
      cloneVSMEReport(id);
    } else if (stdType === 'GHG') {
      cloneGHGReport(id);
    } else if (stdType === 'UAE_FDL') {
      cloneUAEFDLReport(id);
    } else if (stdType === 'BRSR') {
      fetchBRSRReport(id);
    }
  };

  const fetchMetricData = (reportClone, period = null, resetMetricHTMLToDefault = false, periodStart = '', periodEnd = '') => {
    setLoaderDisplay(true);
    StandardService.getMetricData({
      stdId: regulationId,
      period: period,
      standardTypes: ["GRI", 'ESRS'],
      language: report.lang,
      periodStart: periodStart, periodEnd: periodEnd
    })
      .then((res) => {
        let sectionIndex = -1, pageIndex = -1, sustSectionIndex = -1, sustPageIndex = -1;
        for (let i = 0; i < reportClone.sections?.length; i++) {
          const section = reportClone.sections[i];
          for (let j = 0; j < section?.pages?.length; j++) {
            const page = section.pages[j];
            if (page.type === ReportPageType.METRIC_DATA) {
              sectionIndex = i;
              pageIndex = j;
            } else if (page.type === ReportPageType.SUST_CONTENT_INDEX) {
              sustSectionIndex = i;
              sustPageIndex = j;
            }
          }
        }
        if (sectionIndex < 0 && sustSectionIndex < 0) {
          setLoaderDisplay(false);
        } else {
          if (sustSectionIndex >= 0 && sustPageIndex >= 0) {
            reportClone.sections[sustSectionIndex].pages[sustPageIndex].json = res.data;
            if (resetMetricHTMLToDefault) {
              reportClone.sections[sustSectionIndex].pages[sustPageIndex].html = sustIndexPageDefaultHTML;
            }
            renderStandardIndexPage(sustSectionIndex, sustPageIndex, reportClone);
          }
          if (sectionIndex >= 0 && pageIndex >= 0) {
            reportClone.sections[sectionIndex].pages[pageIndex].json = res.data;
            if (resetMetricHTMLToDefault) {
              reportClone.sections[sectionIndex].pages[pageIndex].html = metricDataPageDefaultHTML;
            }
            fetchDiscloureAndUpdateReport(sectionIndex, pageIndex, reportClone);
          }
        }
      })
      .catch((error) => {
        setLoaderDisplay(false);
      });
  };

  const getActiveSectionAndPageIndices = () => {
    const sectionIdx = report.sections?.findIndex(s => s.index === activeSectionIndex) ?? -1;
    const pageIdx = sectionIdx >= 0
      ? report.sections[sectionIdx].pages?.findIndex(p => p.index === activePageIndex) ?? -1
      : -1;
    return { sectionIdx, pageIdx };
  };

  const applyContentToActivePage = (htmlData, jsonData) => {
    const { sectionIdx, pageIdx } = getActiveSectionAndPageIndices();
    if (sectionIdx < 0 || pageIdx < 0) {
      return report;
    }
    return {
      ...report,
      sections: report.sections.map((section, sIndex) => {
        if (sIndex === sectionIdx) {
          return {
            ...section,
            pages: section.pages?.map((page, pIndex) => {
              if (pIndex === pageIdx) {
                return {
                  ...page,
                  html: htmlData,
                  json: jsonData
                };
              }
              return page;
            })
          };
        }
        return section;
      })
    };
  };

  const saveReport = (htmlData, jsonData, options = {}) => {
    const updatedReport = applyContentToActivePage(htmlData, jsonData);
    setReport(updatedReport);
    return createOrUpdateReport(false, updatedReport, options);
  };

  const finalizeReport = (htmlData, jsonData) => {
    const updatedReport = {
      ...applyContentToActivePage(htmlData, jsonData),
      status: ReportStatus.FINALIZED
    };
    setReport(updatedReport);
    return createOrUpdateReport(true, updatedReport);
  };

  const proceedAfterUnsavedPrompt = (shouldNavigate) => {
    const isPageNavigation =
      pausedPageIndexToNavigate.page !== null &&
      pausedPageIndexToNavigate.section !== null;

    if (isPageNavigation) {
      setActiveSectionIndex(pausedPageIndexToNavigate.section);
      setActivePageIndex(pausedPageIndexToNavigate.page);
      setPausedPageIndexToNavigate({ page: null, section: null });
      setShowConfirmation(false);
    } else if (shouldNavigate) {
      confirmNavigation();
    }
  };

  const handleUnsavedChangesConfirm = async (shouldSave) => {
    if (shouldSave) {
      setLoaderDisplay(true);
      try {
        const getContent = reportCardGetContentRef.current;
        if (getContent) {
          const { html, json } = getContent();
          await saveReport(html, json, { skipRefetch: true, pageTargetedUpdate: true });
        }
        setIsContentEdited(false);
        proceedAfterUnsavedPrompt(true);
      } catch (error) {
        toast.error(error?.title || error?.message || "Failed to save report", {
          position: toast.POSITION.TOP_RIGHT,
        });
      } finally {
        setLoaderDisplay(false);
      }
    } else {
      setIsContentEdited(false);
      if (
        pausedPageIndexToNavigate.page !== null &&
        pausedPageIndexToNavigate.section !== null
      ) {
        proceedAfterUnsavedPrompt(false);
      } else {
        cancelNavigation();
      }
    }
  };

  const createOrUpdateReport = (navigateTolist = false, reportToUse = null, options = {}) => {
    const { skipRefetch = false } = options;
    const reportData = JSON.parse(JSON.stringify(reportToUse || report));
    reportData.sections?.forEach(section => {
      section?.pages?.forEach((page, index) => {
        let trimmedHTML = page.html?.replace(/>\s+</g, '><');
        page.htmlContent = trimmedHTML ? window.btoa(unescape(encodeURIComponent(trimmedHTML))) : null;
        page.jsonContent = page.json ? JSON.stringify(page.json) : null;
        delete page.html;
        delete page.json;
        delete page.index;
      });
      delete section.index;
    });

    reportData.regulationId = regulationId;
    reportData.companyId = companyId;

    const handleSaveSuccess = (res) => {
      if (res.status !== 200 && res.status !== 201) {
        return Promise.reject(new Error('Failed to save report'));
      }
      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
      const rId = reportId > 0 ? reportId : res.data.id
      setReportId(rId);
      if (navigateTolist) {
        navigate(buildReportingListUrl({ view: stdType, page: activeTab, listPage, size: listSize }));
      } else if (!skipRefetch) {
        fetchReport(rId);
      }
      return res;
    };

    const handleSaveError = (error) => {
      toast.error(error?.title || error?.message || 'Failed to save report', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return Promise.reject(error);
    };

    if (reportId > 0) {
      reportData.status = ReportStatus.DRAFT;
      if (options.pageTargetedUpdate && activeSectionIndex >= 0 && activePageIndex >= 0) {
        let pagePayload = reportData.sections[activeSectionIndex].pages[activePageIndex];
        return ReportService.updateReportPage(reportData.id, pagePayload.id, pagePayload)
          .then(handleSaveSuccess)
          .catch(handleSaveError);
      }
      if (stdType === 'GRI') {
        return ReportService.updateReport(reportData.id, reportData)
          .then(handleSaveSuccess)
          .catch(handleSaveError);
      }
      if (stdType === 'BRSR') {
        return ReportService.updateBRSRReport(reportData.id, reportData)
          .then(handleSaveSuccess)
          .catch(handleSaveError);
      }
      if (stdType === 'GHG') {
        return ReportService.updateGHGReport(reportData.id, reportData)
          .then(handleSaveSuccess)
          .catch(handleSaveError);
      }
      if (stdType === 'UAE_FDL') {
        reportData.reportType = reportData.reportType || 'UAE_FDL';
        return ReportService.updateReport(reportData.id, reportData)
          .then(handleSaveSuccess)
          .catch(handleSaveError);
      }
      if (stdType === 'ESRS' || stdType === 'VSME') {
        return ReportService.updateESRSReport(reportData.id, reportData)
          .then(handleSaveSuccess)
          .catch(handleSaveError);
      }
      return ReportService.updateReport(reportData.id, reportData)
        .then(handleSaveSuccess)
        .catch(handleSaveError);
    }

    reportData.period = `${yearFilter}-01-01`;
    if (stdType === 'BRSR') {
      return ReportService.createBRSRReport(reportData)
        .then(handleSaveSuccess)
        .catch(handleSaveError);
    }
    if (stdType === 'GHG') {
      return ReportService.createGHGReport(reportData)
        .then(handleSaveSuccess)
        .catch(handleSaveError);
    }
    if (stdType === 'UAE_FDL') {
      reportData.reportType = 'UAE_FDL';
      return ReportService.createESRSReport(reportData)
        .then(handleSaveSuccess)
        .catch(handleSaveError);
    }
    if (stdType === 'ESRS' || stdType === 'VSME') {
      return ReportService.createESRSReport(reportData)
        .then(handleSaveSuccess)
        .catch(handleSaveError);
    }
    return ReportService.createReport(reportData)
      .then(handleSaveSuccess)
      .catch(handleSaveError);
  };

  const updateMetricValue = (topicId, metricId, value) => {
    const page = report.sections[activeSectionIndex]?.pages[activePageIndex];
    const topicIndex = page?.json?.dataPoints?.findIndex(topic => topic.topicId === topicId);
    if (topicIndex >= 0) {
      const metricIndex = page?.json?.dataPoints[topicIndex]?.metricData?.findIndex(metric => metric.metric?.id === metricId);
      if (metricIndex >= 0) {
        const updatedReport = {
          ...report,
          sections: report.sections.map((section, sIndex) => {
            if (sIndex === activeSectionIndex) {
              return {
                ...section,
                pages: section.pages?.map((p, pIndex) => {
                  if (pIndex === activePageIndex) {
                    return {
                      ...p,
                      json: {
                        ...p.json,
                        dataPoints: p.json?.dataPoints?.map((topic, tIdx) => {
                          if (tIdx === topicIndex) {
                            return {
                              ...topic,
                              metricData: topic.metricData?.map((metric, mIdx) => {
                                if (mIdx === metricIndex) {
                                  return {
                                    ...metric,
                                    value: value
                                  };
                                }
                                return metric;
                              })
                            };
                          }
                          return topic;
                        })
                      }
                    };
                  }
                  return p;
                })
              };
            }
            return section;
          })
        };
        setReport(updatedReport);
      }
    }
  };

  const addNewSection = (name) => {
    const index = report.sections.length;
    const newSection = {
      name: name,
      index: index,
      sortOrder: index + 1,
      type: ReportSection.OTHERS,
      pages: []
    };
    const updatedReport = {
      ...report,
      sections: [...report.sections, newSection]
    };
    updateContentsPage(updatedReport);
    setReport(updatedReport);
    setIsContentEdited(false);
    setRenderedOn(Date.now());
    createNewSectionInServer(newSection, updatedReport, updatedReport.sections?.length - 1);
  };

  const addNewPage = (sIndex) => {
    const sectionIndex = report.sections.findIndex(section => section.index === sIndex);
    const pageIndex = report.sections[sectionIndex].pages?.length ?? 0;
    const newPage = {
      name: `New Page`,
      index: pageIndex,
      sortOrder: pageIndex + 1,
      type: ReportPageType.OTHERS,
      html: `
        <div style="font-family: Arial, sans-serif;font-weight:400;">
          <div>
            <h4>New Page</h4>
            <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
          </div>
        </div>
      `,
    };
    const updatedReport = {
      ...report,
      sections: report.sections.map((section, sIdx) => {
        if (sIdx === sectionIndex) {
          return {
            ...section,
            pages: [...(section.pages || []), newPage]
          };
        }
        return section;
      })
    };
    updateContentsPage(updatedReport);
    setReport(updatedReport);
    setRenderedOn(Date.now());
    createNewPageInServer(sectionIndex, newPage, updatedReport, updatedReport.sections[sectionIndex].pages?.length - 1);
  };

  const deleteSection = (index) => {
    const indexToDelete = report.sections.findIndex(s => s.index === index);
    const sectionIdToDelete = report.sections[indexToDelete].id;
    if (indexToDelete === activeSectionIndex) {
      setActiveSectionIndex(0);
      setActivePageIndex(0);
    }
    const updatedReport = {
      ...report,
      sections: report.sections.filter((_, idx) => idx !== indexToDelete)
    };
    refreshSectionAndPageIndices(updatedReport);
    updateContentsPage(updatedReport);
    setReport(updatedReport);
    setRenderedOn(Date.now());
    deleteSectionFromServer(sectionIdToDelete);
  };

  const deletePage = (sIndex, pIndex) => {
    const sectionIndex = report.sections.findIndex(s => s.index === sIndex);
    const sectionIdToDelete = report.sections[sectionIndex].id;
    const pageIndex = report.sections[sectionIndex].pages.findIndex(page => page.index === pIndex);
    const pageIdToDelete = report.sections[sectionIndex].pages[pageIndex].id;
    if (sectionIndex === activeSectionIndex) {
      setActiveSectionIndex(0);
      setActivePageIndex(0);
    }
    const updatedReport = {
      ...report,
      sections: report.sections.map((section, sIdx) => {
        if (sIdx === sectionIndex) {
          return {
            ...section,
            pages: section.pages?.filter((_, pIdx) => pIdx !== pageIndex) || []
          };
        }
        return section;
      })
    };
    refreshSectionAndPageIndices(updatedReport);
    updateContentsPage(updatedReport);
    setReport(updatedReport);
    setRenderedOn(Date.now());
    deletePageFromServer(sectionIdToDelete, pageIdToDelete);
  };

  const updateSection = (sIndex, name) => {
    const sectionIndex = report.sections.findIndex(s => s.index === sIndex);
    const sectionIdToUpdate = report.sections[sectionIndex].id;
    const sectionPayload = {
      ...report.sections[sectionIndex],
      name: name
    };
    const updatedReport = {
      ...report,
      sections: report.sections.map((section, sIdx) => {
        if (sIdx === sectionIndex) {
          return sectionPayload;
        }
        return section;
      })
    };
    updateContentsPage(updatedReport);
    setReport(updatedReport);
    setIsContentEdited(false);
    setRenderedOn(Date.now());
    updateSectionInServer(sectionIdToUpdate, sectionPayload);
  };

  const updatePage = (sIndex, pIndex, name) => {
    const sectionIndex = report.sections.findIndex(s => s.index === sIndex);
    const pageIndex = report.sections[sectionIndex].pages.findIndex(page => page.index === pIndex);
    const pageIdToUpdate = report.sections[sectionIndex].pages[pageIndex].id;
    const pagePayload = {
      ...report.sections[sectionIndex].pages[pageIndex],
      name: name
    };
    const updatedReport = {
      ...report,
      sections: report.sections.map((section, sIdx) => {
        if (sIdx === sectionIndex) {
          return {
            ...section,
            pages: section.pages?.map((page, pIdx) => {
              if (pIdx === pageIndex) {
                return pagePayload;
              }
              return page;
            })
          };
        }
        return section;
      })
    };
    updateContentsPage(updatedReport);
    setReport(updatedReport);
    setRenderedOn(Date.now());
    updatePageInServer(pageIdToUpdate, pagePayload);
  };

  const updateContentsPage = (report) => {
    if (stdType === 'GRI') {
      ReportingUtils.normalizeGriContentsSectionName(report, t('Contents.label'));
      ReportingUtils.applyGriTableOfContentsToReport(report, t('TableofContents.label'));
    }
  };

  const refreshSectionAndPageIndices = (report) => {
    report.sections?.forEach((section, index) => {
      section.index = index;
      section.sortOrder = index + 1;
      section.pages?.forEach((page, pindex) => {
        page.index = pindex;
        page.sortOrder = pindex + 1;
      });
    });
  };

  const previewReport = () => {
    setLoader(true);
    setLoaderDisplay(true);
    ReportService.generateESRSPDF(reportId)
      .then((res) => {
        let blob = new Blob([res.data], { type: 'application/pdf' });
        // Creating new object of PDF file
        const fileURL = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = fileURL;
        link.target = '_blank';
        link.click();
        setLoader(false);
        setLoaderDisplay(false);
      })
      .catch((error) => {
        setLoader(false);
        setLoaderDisplay(false);
        toast.error(error.title, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const fetchDiscloureAndUpdateReport = (sectionIndex, pageIndex, reportClone, periodStart = '', periodEnd = '') => {
    GeneralDisclosureService.getQuestionsAndAnswers("GRI", `${yearFilter}-01-01`, 'en', periodStart, periodEnd)
      .then((res) => {
        if (res.status === 200) {
          const questions = res.data?.data?.questions ?? [];
          renderStandardIndexPage(sectionIndex, pageIndex, reportClone, questions);
        } else {
          renderStandardIndexPage(sectionIndex, pageIndex, reportClone);
        }
        setLoaderDisplay(false);
      })
      .catch((error) => {
        renderStandardIndexPage(sectionIndex, pageIndex, reportClone);
        setLoaderDisplay(false);
      });
  };

  const renderStandardIndexPage = (sectionIndex, pageIndex, reportClone, disclosureQuestions = []) => {
    const pageHTML = reportClone.sections[sectionIndex].pages[pageIndex].html;
    const renderedMetricHTML = renderToStaticMarkup(
      <ReportingStandardData
        page={reportClone.sections[sectionIndex].pages[pageIndex]}
        disclosureQuestions={disclosureQuestions}
        periodStart={reportId > 0 ? Utils.getYearFrom(report.period) : reportPeriodStart}
        periodEnd={reportId > 0 ? Utils.getYearFrom(report.periodEnd) : reportPeriodEnd} />
    );
    const formattedHTML = `${pageHTML}${renderedMetricHTML}`;
    reportClone.sections[sectionIndex].pages[pageIndex].html = formattedHTML;
    setReport(reportClone);
    setRenderedOn(Date.now() + 1);
  };

  const reorderPage = (data) => {
    if (data.page.pageId === data.over.pageId
      && data.page.sectionId === data.over.sectionId) {
      // No effective drag
      return;
    }
    let oldSectionId = 0, newSectionId = 0, movedToPageId = 0;
    // Find and remove the page from source section 
    const srcSectionIndex = report.sections.findIndex(section => section.index === data.page.sectionId);
    if (srcSectionIndex < 0) return;

    const srcPageIndex = report.sections[srcSectionIndex].pages?.findIndex(page => page.index === data.page.pageId);
    if (srcPageIndex < 0) return;

    oldSectionId = report.sections[srcSectionIndex].id;
    const pageToReorder = JSON.parse(JSON.stringify(report.sections[srcSectionIndex].pages[srcPageIndex]));

    // Find and insert the page to destination section
    const destSectionIndex = report.sections.findIndex(section => section.index === data.over.sectionId);
    if (destSectionIndex < 0) return;

    newSectionId = report.sections[destSectionIndex].id;
    const destPageIndex = report.sections[destSectionIndex].pages?.findIndex(page => page.index === data.over.pageId);
    if (destPageIndex < 0) return;

    movedToPageId = report.sections[destSectionIndex].pages[destPageIndex].id;

    // Create updated report with immutable updates
    const updatedReport = {
      ...report,
      sections: report.sections.map((section, sIdx) => {
        if (sIdx === srcSectionIndex) {
          // Remove page from source section and update sort orders
          const updatedPages = section.pages?.filter((_, pIdx) => pIdx !== srcPageIndex) || [];
          return {
            ...section,
            pages: updatedPages.map((p, index) => ({
              ...p,
              sortOrder: index + 1
            }))
          };
        } else if (sIdx === destSectionIndex) {
          // Add page to destination section and update sort orders
          const pagesBefore = section.pages?.slice(0, destPageIndex) || [];
          const pagesAfter = section.pages?.slice(destPageIndex) || [];
          const updatedPages = [...pagesBefore, pageToReorder, ...pagesAfter];
          return {
            ...section,
            pages: updatedPages.map((p, index) => ({
              ...p,
              sortOrder: index + 1
            }))
          };
        }
        return section;
      })
    };

    updateContentsPage(updatedReport);
    setReport(updatedReport);
    if (reportId > 0) {
      const payload = {
        movedToPageId: movedToPageId,
        sectionId: newSectionId,
        oldSectionId: oldSectionId
      };
      updatePageOrder(pageToReorder.id, payload, updatedReport)
    } else {
      createOrUpdateReport(false, updatedReport);
    }
    setRenderedOn(Date.now());
  };

  const updatePageOrder = (pageId, payload, report) => {
    let contentPage;
    for (let i = 0; i < report?.sections?.length; i++) {
      const section = report?.sections[i];
      if (section.type === ReportSection.CONTENTS) {
        contentPage = section.pages?.find(
          (p) => p.type === ReportPageType.CONTENTS || p.type === 'CONTENTS',
        ) || section.pages[0];
        break;
      }
    }
    let trimmedHTML = contentPage.html?.replace(/>\s+</g, '><');
    contentPage.htmlContent = trimmedHTML ? window.btoa(unescape(encodeURIComponent(trimmedHTML))) : null;
    payload.contentPage = contentPage;
    ReportService.reorderPage(reportId, pageId, payload).then(res => {
      if (res.status === 200) {
        toast.success("Success", {
          position: toast.POSITION.TOP_RIGHT,
        });
        fetchReport(reportId);
      }
    }).catch(error => {
      toast.error(error.title, {
        position: toast.POSITION.TOP_RIGHT,
      });
    });
  };

  const getActivePage = () => {
    const sIndex = report.sections?.findIndex(s => s.index === activeSectionIndex);
    if (sIndex < 0) return report.sections[0].pages[0];

    const pIndex = report.sections[sIndex].pages?.findIndex(p => p.index === activePageIndex);
    if (pIndex < 0) return report.sections[sIndex].pages[0];

    return report.sections[sIndex].pages[pIndex];
  };

  const getActiveSection = () => {
    const sIndex = report.sections?.findIndex(s => s.index === activeSectionIndex);
    if (sIndex < 0) return report.sections[0];

    return report.sections[sIndex];
  };

  const addNewPageFromSuggestion = (p) => {
    const activeSection = getActiveSection();
    const pagelength = activeSection.pages?.length;
    const lastPageInsection = activeSection.pages[pagelength - 1];
    const newIndex = lastPageInsection ? lastPageInsection.index + 1 : 0;
    const newSortOrder = lastPageInsection ? lastPageInsection.sortOrder + 1 : 1;
    p.index = newIndex;
    p.sortOrder = newSortOrder;
    const reportClone = JSON.parse(JSON.stringify(report));
    reportClone.sections[activeSection.index].pages.push(p);
    updateContentsPage(reportClone);
    setReport(reportClone);
    setRenderedOn(Date.now());
  };

  const checkStdTypeRepotingSidebar = (stdType) => {
    if (stdType == 'GRI') {
      return (<><ReportingSidebar sections={report?.sections}
        activeSectionIndex={activeSectionIndex}
        activePageIndex={activePageIndex}
        onPageChange={(sIndex, pIndex) => {
          if (isContentEdited) {
            setPausedPageIndexToNavigate({ page: pIndex, section: sIndex });
            setShowConfirmation(true);
          } else {
            setActiveSectionIndex(sIndex);
            setActivePageIndex(pIndex);
            setIsContentEdited(false);
          }
        }}
        onAddingNewSection={(name) => addNewSection(name)}
        onAddingNewPage={(index) => addNewPage(index)}
        onDeletingSection={(index) => deleteSection(index)}
        onDeletingPage={(s, p) => deletePage(s, p)}
        onUpdatingSection={(index, name) => updateSection(index, name)}
        onUpdatingPage={(s, p, name) => updatePage(s, p, name)}
        onContentEdited={(val) => setIsContentEdited(val)}
        onReorderPage={(d) => reorderPage(d)}
        onAddingNewPageFromSuggestion={(p) => addNewPageFromSuggestion(p)} />
        <div className="row m-0">
          {
            report && report?.sections?.length > 0 &&
            <ReportingCard key={`reportCard-${activeSectionIndex}-${activePageIndex}`}
              getContentRef={reportCardGetContentRef}
              companyId={companyId}
              reportName={report.name}
              page={getActivePage()}
              isPreview={mode === 'VIEW'}
              reportId={reportId}
              section={getActiveSection()}
              yearFilter={reportId > 0 ? Utils.getYearFrom(report.period) : yearFilter}
              regulationId={reportId > 0 ? report.regulationId : +regulationId}
              regulationName={reportId > 0 ? report.regulationName : regulationName}
              regulationFrequency={regulationFrequency}
              reportFrequency={reportId > 0 ? report.frequency : null}
              onSave={(htmlData, jsonData) => {
                saveReport(htmlData, jsonData, { pageTargetedUpdate: true, skipRefetch: true });
                setIsContentEdited(false);
              }}
              onFinalize={(htmlData, jsonData) => {
                finalizeReport(htmlData, jsonData);
                setIsContentEdited(false);
              }}
              onCancel={() => {
                setIsContentEdited(false);
              }}
              onMetricValueChange={(topicId, metricId, value) => updateMetricValue(topicId, metricId, value)}
              onContentEdited={() => setIsContentEdited(true)}
              onPreview={() => previewReport()}
              onPageChange={(sIndex, pIndex) => {
                if (isContentEdited) {
                  setPausedPageIndexToNavigate({ page: pIndex, section: sIndex });
                  setShowConfirmation(true);
                } else {
                  setActiveSectionIndex(sIndex);
                  setActivePageIndex(pIndex);
                  setIsContentEdited(false);
                }
              }}
              sections={report?.sections}
              onReportingPeriodChange={(year) => fetchMetricData(JSON.parse(JSON.stringify(report)), `${year}-01-01`, true)}
              reportType={report.type}
              jobId={jobId}
              loader={Loader}
              reportingStandard={stdType}
              language={report.lang}
              periodStart={reportId > 0 ? report.period : reportPeriodStart}
              periodEnd={reportId > 0 ? report.periodEnd : reportPeriodEnd}
              entities={reportId > 0 ? report.entities : reportEntities}
              source={source}
              activeTab={activeTab}
              listPage={listPage}
              listSize={listSize}
            />
          }
        </div> </>)
    } else if (stdType == 'BRSR') {
      return (<><BRSRReportingSidebar sections={report?.sections}
        activeSectionIndex={activeSectionIndex}
        language={report?.lang || i18n.language}
        onPageChange={(sIndex, pIndex) => {
          if (isContentEdited) {
            setPausedPageIndexToNavigate({ page: pIndex, section: sIndex });
            setShowConfirmation(true);
          } else {
            setActiveSectionIndex(sIndex);
            setActivePageIndex(pIndex);
            setIsContentEdited(false);
          }
        }} />
        <div className="row m-0">
          {
            report && report?.sections?.length > 0 &&
            <BRSRReportingCard key={`reportCard-${activeSectionIndex}-${activePageIndex}`}
              getContentRef={reportCardGetContentRef}
              reportName={report.name}
              page={getActivePage()}
              section={getActiveSection()}
              reportId={reportId}
              yearFilter={reportId > 0 ? Utils.getYearFrom(report.period) : yearFilter}
              regulationId={reportId > 0 ? report.regulationId : +regulationId}
              regulationName={reportId > 0 ? report.regulationName : regulationName}
              regulationFrequency={regulationFrequency}
              reportFrequency={reportId > 0 ? report.frequency : null}
              onSave={(htmlData, jsonData) => {
                saveReport(htmlData, jsonData, { pageTargetedUpdate: true, skipRefetch: true });
                setIsContentEdited(false);
              }}
              onFinalize={(htmlData, jsonData) => {
                finalizeReport(htmlData, jsonData);
                setIsContentEdited(false);
              }}
              onCancel={() => {
                setIsContentEdited(false);
              }}
              onContentEdited={() => setIsContentEdited(true)}
              report={report}
              jobId={jobId}
              loader={Loader}
              periodStart={reportId > 0 ? report.period : reportPeriodStart}
              periodEnd={reportId > 0 ? report.periodEnd : reportPeriodEnd}
              entities={reportId > 0 ? report.entities : reportEntities}
              language={report?.lang || i18n.language}
              activeTab={activeTab}
              listPage={listPage}
              listSize={listSize}
            />
          }
        </div> </>)
    } else {
      return (<><ESRSReportingSidebar sections={report?.sections}
        activeSectionIndex={activeSectionIndex}
        activePageIndex={activePageIndex}
        language={report?.lang || i18n.language}
        onPageChange={(sIndex, pIndex) => {
          if (isContentEdited) {
            setPausedPageIndexToNavigate({ page: pIndex, section: sIndex });
            setShowConfirmation(true);
          } else {
            setActiveSectionIndex(sIndex);
            setActivePageIndex(pIndex);
            setIsContentEdited(false);
          }
        }}
        onAddingNewSection={(name) => addNewSection(name)}
        onAddingNewPage={(index) => addNewPage(index)}
        onDeletingSection={(index) => deleteSection(index)}
        onDeletingPage={(s, p) => deletePage(s, p)}
        onUpdatingSection={(index, name) => updateSection(index, name)}
        onUpdatingPage={(s, p, name) => updatePage(s, p, name)}
        onContentEdited={(val) => setIsContentEdited(val)}
        onReorderPage={(d) => reorderPage(d)}
        onAddingNewPageFromSuggestion={(p) => addNewPageFromSuggestion(p)} />
        <div className="row m-0">
          {
            report && report?.sections?.length > 0 &&
            <ESRSReportingCard key={`reportCard-${activeSectionIndex}-${activePageIndex}`}
              getContentRef={reportCardGetContentRef}
              stdType={stdType}
              companyId={companyId}
              reportName={report.name}
              page={getActivePage()}
              isPreview={mode === 'VIEW'}
              reportId={reportId}
              section={getActiveSection()}
              yearFilter={reportId > 0 ? Utils.getYearFrom(report.period) : yearFilter}
              regulationId={reportId > 0 ? report.regulationId : +regulationId}
              regulationName={reportId > 0 ? report.regulationName : regulationName}
              regulationFrequency={regulationFrequency}
              reportFrequency={reportId > 0 ? report.frequency : null}
              onSave={(htmlData, jsonData) => {
                saveReport(htmlData, jsonData, { pageTargetedUpdate: true, skipRefetch: true });
                setIsContentEdited(false);
              }}
              onFinalize={(htmlData, jsonData) => {
                finalizeReport(htmlData, jsonData);
                setIsContentEdited(false);
              }}
              onCancel={() => {
                setIsContentEdited(false);
              }}
              onMetricValueChange={(topicId, metricId, value) => updateMetricValue(topicId, metricId, value)}
              onContentEdited={() => setIsContentEdited(true)}
              onPreview={() => previewReport()}
              onReportingPeriodChange={(year) => fetchMetricData(JSON.parse(JSON.stringify(report)), `${year}-01-01`, true)}
              reportType={report.type}
              jobId={jobId}
              loader={Loader}
              activeTab={activeTab}
              reportingStandard={stdType}
              language={report.lang}
              periodStart={reportId > 0 ? report.period : reportPeriodStart}
              periodEnd={reportId > 0 ? report.periodEnd : reportPeriodEnd}
              entities={reportId > 0 ? report.entities : reportEntities}
              source={source}
              listPage={listPage}
              listSize={listSize}
            />
          }
        </div></>)
    }
  };

  const cloneVSMEReport = (id) => {
    ReportService.getESRSReport(id > 0 ? id : reportId).then(res => {
      if (res.status === 200) {
        const reportClone = res.data;
        const page1Req = ReportService.getReportV2Template({ pageId: 1, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page2Req = ReportService.getReportV2Template({ pageId: 2, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page3Req = ReportService.getReportV2Template({ pageId: 3, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page4Req = ReportService.getReportV2Template({ pageId: 4, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page5Req = ReportService.getReportV2Template({ pageId: 5, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page6Req = ReportService.getReportV2Template({ pageId: 6, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page7Req = ReportService.getReportV2Template({ pageId: 7, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page8Req = ReportService.getReportV2Template({ pageId: 8, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page9Req = ReportService.getReportV2Template({ pageId: 9, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page10Req = ReportService.getReportV2Template({ pageId: 10, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page11Req = ReportService.getReportV2Template({ pageId: 11, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page12Req = ReportService.getReportV2Template({ pageId: 12, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page13Req = ReportService.getReportV2Template({ pageId: 13, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page14Req = ReportService.getReportV2Template({ pageId: 14, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page15Req = ReportService.getReportV2Template({ pageId: 15, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page16Req = ReportService.getReportV2Template({ pageId: 16, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page17Req = ReportService.getReportV2Template({ pageId: 17, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page18Req = ReportService.getReportV2Template({ pageId: 18, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page19Req = ReportService.getReportV2Template({ pageId: 19, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        const page20Req = ReportService.getReportV2Template({ pageId: 20, type: 'VSME', standardId: regulationId, period: reportClone.period, answerCheck: true, periodEnd: reportClone.periodEnd, buIds: reportClone.entities, rFreq: reportClone.frequency })
        Promise.all([
          page1Req,
          page2Req,
          page3Req,
          page4Req,
          page5Req,
          page6Req,
          page7Req,
          page8Req,
          page9Req,
          page10Req,
          page11Req,
          page12Req,
          page13Req,
          page14Req,
          page15Req,
          page16Req,
          page17Req,
          page18Req,
          page19Req,
          page20Req,
        ]).then(res => {
          const templateMapping = {
            B1: !!res[0].data.data ? res[0].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            B2: !!res[1].data.data ? res[1].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            B3: !!res[2].data.data ? res[2].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            B4: !!res[3].data.data ? res[3].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            B5: !!res[4].data.data ? res[4].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            B6: !!res[5].data.data ? res[5].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            B7: !!res[6].data.data ? res[6].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            B8: !!res[7].data.data ? res[7].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            B9: !!res[8].data.data ? res[8].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            B10: !!res[9].data.data ? res[9].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            B11: !!res[10].data.data ? res[10].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            C1: !!res[11].data.data ? res[11].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            C2: !!res[12].data.data ? res[12].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            C3: !!res[13].data.data ? res[13].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            C4: !!res[14].data.data ? res[14].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            C5: !!res[15].data.data ? res[15].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            C6: !!res[16].data.data ? res[16].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            C7: !!res[17].data.data ? res[17].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            C8: !!res[18].data.data ? res[18].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            C9: !!res[19].data.data ? res[19].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
          };
          reportClone.sections?.sort((a, b) => {
            return a.sortOrder - b.sortOrder;
          });
          if (isCoDraftMode) {
            reportClone.status = ReportStatus.DRAFT;
          }
          reportClone.sections?.forEach((section, index) => {
            section.index = index;
            section.type = ReportSection[section.type];
            section.pages?.sort((a, b) => {
              return a.sortOrder - b.sortOrder;
            });
            section.pages?.forEach((page, index) => {
              let decodedHTML = page.htmlContent ? decodeURIComponent(escape(window.atob(page.htmlContent))) : null;
              let templateD = templateMapping[page.type] || [];
              if (templateD.length > 0) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(decodedHTML, "text/html");
                for (let index = 0; index < templateD.length; index++) {
                  const element = templateD[index];
                  if (!element.key.trim().includes(" ") && element.keyValueType == "SINGLE_VALUE" && element.keyValues.length > 0) {
                    const date1 = new Date(element.keyValues[0].latestDate)
                    const date2 = new Date(reportClone.lastModifiedDate)
                    if (!!element.keyValues[0].latestDate && date2 < date1) {
                      const safeKey = CSS.escape(element.key);
                      const items = doc.querySelector(`.${safeKey}`);
                      if (!!items) {
                        const answer = element.keyValues[0]
                        items.innerText = !!answer.uom ? answer.uom == "no." ? parseInt(answer.value) : answer.value + ' ' + answer.uom : answer.value;
                        decodedHTML = doc.body.innerHTML;
                      }
                    }
                  }
                  if (!element.key.trim().includes(" ") && element.keyValueType == "TYPE_1_TABLE" || !element.key.trim().includes(" ") && element.keyValueType == "TYPE_2_TABLE" || !element.key.trim().includes(" ") && element.keyValueType == "TYPE_3_TABLE") {
                    for (let i = 0; i < element.keyValues.length; i++) {
                      const element1 = element.keyValues[i];
                      const date1 = new Date(element1.latestDate)
                      const date2 = new Date(reportClone.lastModifiedDate)
                      const tableKey = [];
                      if (element.keyValueType == "TYPE_3_TABLE") {
                        if (tableKey.indexOf(element1.key) == -1) {
                          const filterKeyValue = element.keyValues.filter(itm => itm.key == element1.key)
                          const safeKey = CSS.escape(element1.key);
                          const items = doc.querySelectorAll(`.${safeKey}`);
                          if (items.length > 0) {
                            items.forEach((item, index) => {
                              const answer = filterKeyValue[index]
                              const date3 = new Date(answer.latestDate)
                              if (!!answer.latestDate && date2 < date3) {
                                item.innerText = !!answer.uom ? answer.uom == "no." ? parseInt(answer.value) : answer.value + ' ' + answer.uom : answer.value;
                              }
                            });
                            decodedHTML = doc.body.innerHTML;
                          }
                          tableKey.push(element1.key)
                        }
                      } else if (!!element1.latestDate && date2 < date1) {
                        const safeKey = CSS.escape(element1.key);
                        const items = doc.querySelectorAll(`.${safeKey}`);
                        if (items.length > 0) {
                          items.forEach((item, k) => {
                            item.innerText = !!element1.uom ? element1.uom == "no." ? parseInt(element1.value) : element1.value + ' ' + element1.uom : element1.value;
                          });
                          decodedHTML = doc.body.innerHTML;
                        }
                      }

                    }
                  }
                }
              }
              page.html = decodedHTML;
              page.json = page.jsonContent ? JSON.parse(page.jsonContent) : null;
              page.section = ReportSection[page.section];
              page.index = index;
              page.type = ReportPageType[page.type];
            });
          });
          setReport(reportClone);
          setLoaderDisplay(false);
        }).catch(err => {
          toast.error("Error fetching template data", {
            position: toast.POSITION.TOP_RIGHT,
          });
          setLoaderDisplay(false);
        });
      }
    }).catch(error => {
      setLoaderDisplay(false);
      toast.error(error.title, {
        position: toast.POSITION.TOP_RIGHT,
      });
    });
  };

  const cloneGHGReport = async (id) => {
    ReportService.getGHGReport(id > 0 ? id : reportId).then(async res => {
      if (res.status === 200) {
        const reportClone = res.data;

        await ensureLanguageLoaded(i18n, reportClone.lang || i18n.language);

        const templateParams = {
          type: 'GHG',
          standardId: regulationId,
          period: reportClone.period,
          answerCheck: true,
          periodEnd: reportClone.periodEnd,
          buIds: reportClone.entities,
          rFreq: reportClone.frequency,
        };
        const page1Req = ReportService.getReportV2Template({ pageId: 1, ...templateParams });
        const page2Req = ReportService.getReportV2Template({ pageId: 2, ...templateParams });
        const page3Req = ReportService.getReportV2Template({ pageId: 3, ...templateParams });
        const page4Req = ReportService.getReportV2Template({ pageId: 4, ...templateParams });
        const page5Req = ReportService.getReportV2Template({ pageId: 5, ...templateParams });
        const page6Req = ReportService.getReportV2Template({ pageId: 6, ...templateParams });
        const page7Req = ReportService.getReportV2Template({ pageId: 7, ...templateParams });
        const page8Req = ReportService.getReportV2Template({ pageId: 8, ...templateParams });
        const page9Req = ReportService.getReportV2Template({ pageId: 9, ...templateParams });
        Promise.all([page1Req, page2Req, page3Req, page4Req, page5Req, page6Req, page7Req, page8Req, page9Req]).then(res => {
          const templateMapping = {
            OVERVIEW_HIGHLIGHTS: !!res[0].data.data ? res[0].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            REPORT_BASIS_SCOPE: !!res[1].data.data ? res[1].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            ORGANISATIONAL_STRUCTURE_BOUNDARIES: !!res[2].data.data ? res[2].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            REPORTING_BASE_YEAR_APPROACH: !!res[3].data.data ? res[3].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            EMISSIONS_ACCOUNTING: !!res[4].data.data ? res[4].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            ENERGY_MIX_EMISSION_SOURCES: !!res[5].data.data ? res[5].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            GHG_MANAGEMENT_PERFORMANCE: !!res[6].data.data ? res[6].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            VERIFICATION_INVENTORY_QUALITY: !!res[7].data.data ? res[7].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            REFERENCES_SUPPORTING_MATERIALS: !!res[8].data.data ? res[8].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
          };
          reportClone.sections?.sort((a, b) => {
            return a.sortOrder - b.sortOrder;
          });
          if (isCoDraftMode) {
            reportClone.status = ReportStatus.DRAFT;
          }
          reportClone.sections?.forEach((section, index) => {
            section.index = index;
            section.type = ReportSection[section.type];
            section.pages?.sort((a, b) => {
              return a.sortOrder - b.sortOrder;
            });
            section.pages?.forEach((page, index) => {
              let decodedHTML = page.htmlContent ? decodeURIComponent(escape(window.atob(page.htmlContent))) : null;
              let templateD = templateMapping[page.type] || [];
              if (templateD.length > 0) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(decodedHTML, "text/html");
                for (let index = 0; index < templateD.length; index++) {
                  const element = templateD[index];
                  if (!element.key.trim().includes(" ") && element.keyValueType == "SINGLE_VALUE" && element.keyValues.length > 0) {
                    const date1 = new Date(element.keyValues[0].latestDate)
                    const date2 = new Date(reportClone.lastModifiedDate)
                    if (!!element.keyValues[0].latestDate && date2 < date1) {
                      const safeKey = CSS.escape(element.key);
                      const items = doc.querySelector(`.${safeKey}`);
                      if (!!items) {
                        const answer = element.keyValues[0]
                        items.innerText = !!answer.uom ? answer.uom == "no." ? parseInt(answer.value) : answer.value + ' ' + answer.uom : answer.value;
                        decodedHTML = doc.body.innerHTML;
                      }
                    }
                  }
                  if (!element.key.trim().includes(" ") && element.keyValueType == "TYPE_1_TABLE" || !element.key.trim().includes(" ") && element.keyValueType == "TYPE_2_TABLE" || !element.key.trim().includes(" ") && element.keyValueType == "TYPE_3_TABLE") {
                    for (let i = 0; i < element.keyValues.length; i++) {
                      const element1 = element.keyValues[i];
                      const date1 = new Date(element1.latestDate)
                      const date2 = new Date(reportClone.lastModifiedDate)
                      const tableKey = [];
                      if (element.keyValueType == "TYPE_3_TABLE") {
                        if (tableKey.indexOf(element1.key) == -1) {
                          const filterKeyValue = element.keyValues.filter(itm => itm.key == element1.key)
                          const safeKey = CSS.escape(element1.key);
                          const items = doc.querySelectorAll(`.${safeKey}`);
                          if (items.length > 0) {
                            items.forEach((item, index) => {
                              const answer = filterKeyValue[index]
                              const date3 = new Date(answer.latestDate)
                              if (!!answer.latestDate && date2 < date3) {
                                item.innerText = !!answer.uom ? answer.uom == "no." ? parseInt(answer.value) : answer.value + ' ' + answer.uom : answer.value;
                              }
                            });
                            decodedHTML = doc.body.innerHTML;
                          }
                          tableKey.push(element1.key)
                        }
                      } else if (!!element1.latestDate && date2 < date1) {
                        const safeKey = CSS.escape(element1.key);
                        const items = doc.querySelectorAll(`.${safeKey}`);
                        if (items.length > 0) {
                          items.forEach((item, k) => {
                            // Format value to 4 decimal places without unit
                            const formattedValue = Utils.roundToDecimalPlacesIfNumber(element1.value, 4);
                            item.innerText = formattedValue;
                          });
                          decodedHTML = doc.body.innerHTML;
                        }
                      }

                      // Handle BASE_YEAR CODE_MAPPING cells (Total column) for TYPE_2_TABLE - format without unit and 4 decimals
                      if (element.keyValueType == "TYPE_2_TABLE" && element1.dataPeriod === 'BASE_YEAR' && element1.metadata && element1.metadata.columnType === "CODE_MAPPING") {
                        const safeKey = CSS.escape(element1.key);
                        const items = doc.querySelectorAll(`.${safeKey}`);
                        if (items.length > 0) {
                          items.forEach((item, k) => {
                            // Format value to 4 decimal places without unit
                            const formattedValue = Utils.roundToDecimalPlacesIfNumber(element1.value, 4);
                            item.innerText = formattedValue;
                          });
                          decodedHTML = doc.body.innerHTML;
                        }
                      }

                      // Handle BASE_YEAR emission factors for TYPE_2_TABLE
                      if (element.keyValueType == "TYPE_2_TABLE" && element1.dataPeriod === 'BASE_YEAR' && element1.emissionFactors && element1.emissionFactors.length > 0) {
                        // Extract row number from element1's metadata columnNumber (e.g., "B2" -> 2)
                        const rowNum = element1.metadata && element1.metadata.columnNumber
                          ? parseInt(element1.metadata.columnNumber.replace(/[A-Z]/, ''))
                          : null;

                        if (rowNum) {
                          // Process each emission factor
                          element1.emissionFactors.forEach(ef => {
                            if (ef.ghg && ef.factor !== undefined && ef.factor !== null) {
                              // Find the column header for this GHG type
                              const headerRow = doc.querySelector('thead tr');
                              if (headerRow) {
                                const headerCells = headerRow.querySelectorAll('th');
                                let colIndex = -1;
                                headerCells.forEach((headerCell, idx) => {
                                  if (headerCell.textContent.trim() === ef.ghg) {
                                    colIndex = idx;
                                  }
                                });

                                // Find the data row (rowNum - 2 because: rowNum 2 = index 0, rowNum 3 = index 1, etc.)
                                if (colIndex >= 0) {
                                  const dataRows = doc.querySelectorAll('tbody tr');
                                  const rowIndex = rowNum - 2; // Convert rowNum (2,3,4) to array index (0,1,2)

                                  if (dataRows[rowIndex]) {
                                    const rowCells = dataRows[rowIndex].querySelectorAll('td');
                                    if (rowCells[colIndex]) {
                                      const factorValue = Utils.roundToDecimalPlacesIfNumber(ef.factor, 4);
                                      rowCells[colIndex].innerText = factorValue;
                                      decodedHTML = doc.body.innerHTML;
                                    }
                                  }
                                }
                              }
                            }
                          });
                        }
                      }

                    }
                  }
                  if (shouldPreserveSavedGHGTable(doc, element)) {
                    continue;
                  }
                  if (element.keyValueType == "TYPE_5_TABLE") {
                    const typeTableHolder = doc.getElementById(`typeTableHolder-${element.key}`);
                    if (!!typeTableHolder) {
                      const newHtml = pagetableTYPE_5Function(element, normalizeLanguageCode(reportClone.lang), { decimalPlaces: companyInfo.decimalPlaces });
                      // Replace only the specific div with new HTML
                      typeTableHolder.outerHTML = newHtml;
                      decodedHTML = doc.body.innerHTML;
                    }
                  }
                  if (element.keyValueType == "TYPE_6_TABLE") {
                    try {
                      const typeTableHolder = doc.getElementById(`typeTableHolder-${element.key}`);
                      if (!!typeTableHolder) {
                        const newHtml = pagetableTYPE_6Function(element, normalizeLanguageCode(reportClone.lang), { decimalPlaces: companyInfo.decimalPlaces });
                        // Replace only the specific div with new HTML
                        typeTableHolder.outerHTML = newHtml;
                        decodedHTML = doc.body.innerHTML;
                      } else {
                        // If placeholder doesn't exist, insert the table HTML directly
                        // Find where to insert (after the last element or at the end of body)
                        const newHtml = pagetableTYPE_6Function(element, normalizeLanguageCode(reportClone.lang), { decimalPlaces: companyInfo.decimalPlaces });
                        const tempDiv = doc.createElement('div');
                        tempDiv.innerHTML = newHtml;
                        if (doc.body.lastChild) {
                          doc.body.insertBefore(tempDiv.firstChild, doc.body.lastChild.nextSibling);
                        } else {
                          doc.body.appendChild(tempDiv.firstChild);
                        }
                        decodedHTML = doc.body.innerHTML;
                      }
                    } catch (error) {
                      console.error('Error rendering TYPE_6_TABLE:', error, element);
                    }
                  }

                  if (element.keyValueType == "TYPE_7_TABLE") {
                    const newHtml = pagetableTYPE_7Function(element, normalizeLanguageCode(reportClone.lang), { decimalPlaces: companyInfo?.decimalPlaces });
                    const typeTableHolder = doc.getElementById(`typeTableHolder-${element.key}`);
                    if (!!typeTableHolder) {
                      typeTableHolder.outerHTML = newHtml;
                    } else {
                      const wrap = doc.createElement('div');
                      wrap.innerHTML = newHtml;
                      while (wrap.firstChild) {
                        doc.body.appendChild(wrap.firstChild);
                      }
                    }
                    decodedHTML = doc.body.innerHTML;
                  }
                }
              }
              page.html = decodedHTML;
              page.json = page.jsonContent ? JSON.parse(page.jsonContent) : null;
              page.section = ReportSection[page.section];
              page.index = index;
              page.type = ReportPageType[page.type];
            });
          });
          setReport(reportClone);
          setLoaderDisplay(false);
        }).catch(err => {
          toast.error("Error fetching template data", {
            position: toast.POSITION.TOP_RIGHT,
          });
          setLoaderDisplay(false);
        });
      }
    }).catch(error => {
      setLoaderDisplay(false);
      toast.error(error.title, {
        position: toast.POSITION.TOP_RIGHT,
      });
    });
  };

  const cloneUAEFDLReport = async (id) => {
    ReportService.getReport(id > 0 ? id : reportId).then(async res => {
      if (res.status === 200) {
        const reportClone = res.data;

        await ensureLanguageLoaded(i18n, reportClone.lang || i18n.language);

        const templateParams = {
          type: 'UAE_FDL',
          standardId: regulationId,
          period: reportClone.period,
          answerCheck: true,
          periodEnd: reportClone.periodEnd,
          buIds: reportClone.entities,
          rFreq: reportClone.frequency,
        };
        const page1Req = ReportService.getReportV2Template({ pageId: 1, ...templateParams });
        const page2Req = ReportService.getReportV2Template({ pageId: 2, ...templateParams });
        const page3Req = ReportService.getReportV2Template({ pageId: 3, ...templateParams });
        const page4Req = ReportService.getReportV2Template({ pageId: 4, ...templateParams });
        const page5Req = ReportService.getReportV2Template({ pageId: 5, ...templateParams });
        const page6Req = ReportService.getReportV2Template({ pageId: 6, ...templateParams });
        const page7Req = ReportService.getReportV2Template({ pageId: 7, ...templateParams });
        Promise.all([page1Req, page2Req, page3Req, page4Req, page5Req, page6Req, page7Req]).then(res => {
          const templateMapping = {
            UAE_FDL_ABOUT_THE_ORGANIZATION: !!res[0].data.data ? res[0].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            UAE_FDL_OBJECTIVES_OF_DECREE_LAW: !!res[1].data.data ? res[1].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            UAE_FDL_GHG_EMISSIONS_INVENTORY: !!res[2].data.data ? res[2].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            UAE_FDL_INFORMATION_ABOUT_EMISSIONS: !!res[3].data.data ? res[3].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            UAE_FDL_DECARBONIZATION_TARGETS_STRATEGY: !!res[4].data.data ? res[4].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            UAE_FDL_VERIFICATION_COMPLIANCE: !!res[5].data.data ? res[5].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
            UAE_FDL_INSIGHTS: !!res[6].data.data ? res[6].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [],
          };
          reportClone.sections?.sort((a, b) => {
            return a.sortOrder - b.sortOrder;
          });
          if (isCoDraftMode) {
            reportClone.status = ReportStatus.DRAFT;
          }
          reportClone.sections?.forEach((section, index) => {
            section.index = index;
            section.type = typeof section.type === 'number'
              ? section.type
              : ReportSection[section.type];
            section.pages?.sort((a, b) => {
              return a.sortOrder - b.sortOrder;
            });
            section.pages?.forEach((page, index) => {
              let decodedHTML = page.htmlContent ? decodeURIComponent(escape(window.atob(page.htmlContent))) : null;
              const pageTypeKey = typeof page.type === 'number'
                ? Object.keys(ReportPageType).find((key) => ReportPageType[key] === page.type)
                : page.type;
              let templateD = templateMapping[pageTypeKey] || [];
              if (templateD.length > 0) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(decodedHTML, "text/html");
                for (let index = 0; index < templateD.length; index++) {
                  const element = templateD[index];
                  if (!element.key.trim().includes(" ") && element.keyValueType == "SINGLE_VALUE" && element.keyValues.length > 0) {
                    const date1 = new Date(element.keyValues[0].latestDate)
                    const date2 = new Date(reportClone.lastModifiedDate)
                    if (!!element.keyValues[0].latestDate && date2 < date1) {
                      const safeKey = CSS.escape(element.key);
                      const items = doc.querySelector(`.${safeKey}`);
                      if (!!items) {
                        const answer = element.keyValues[0]
                        items.innerText = !!answer.uom ? answer.uom == "no." ? parseInt(answer.value) : answer.value + ' ' + answer.uom : answer.value;
                        decodedHTML = doc.body.innerHTML;
                      }
                    }
                  }
                  if (!element.key.trim().includes(" ") && element.keyValueType == "TYPE_1_TABLE" || !element.key.trim().includes(" ") && element.keyValueType == "TYPE_2_TABLE" || !element.key.trim().includes(" ") && element.keyValueType == "TYPE_3_TABLE") {
                    for (let i = 0; i < element.keyValues.length; i++) {
                      const element1 = element.keyValues[i];
                      const date1 = new Date(element1.latestDate)
                      const date2 = new Date(reportClone.lastModifiedDate)
                      const tableKey = [];
                      if (element.keyValueType == "TYPE_3_TABLE") {
                        if (tableKey.indexOf(element1.key) == -1) {
                          const filterKeyValue = element.keyValues.filter(itm => itm.key == element1.key)
                          const safeKey = CSS.escape(element1.key);
                          const items = doc.querySelectorAll(`.${safeKey}`);
                          if (items.length > 0) {
                            items.forEach((item, index) => {
                              const answer = filterKeyValue[index]
                              const date3 = new Date(answer.latestDate)
                              if (!!answer.latestDate && date2 < date3) {
                                item.innerText = !!answer.uom ? answer.uom == "no." ? parseInt(answer.value) : answer.value + ' ' + answer.uom : answer.value;
                              }
                            });
                            decodedHTML = doc.body.innerHTML;
                          }
                          tableKey.push(element1.key)
                        }
                      } else if (!!element1.latestDate && date2 < date1) {
                        const safeKey = CSS.escape(element1.key);
                        const items = doc.querySelectorAll(`.${safeKey}`);
                        if (items.length > 0) {
                          items.forEach((item) => {
                            const formattedValue = Utils.roundToDecimalPlacesIfNumber(element1.value, 4);
                            item.innerText = formattedValue;
                          });
                          decodedHTML = doc.body.innerHTML;
                        }
                      }

                      if (element.keyValueType == "TYPE_2_TABLE" && element1.dataPeriod === 'BASE_YEAR' && element1.metadata && element1.metadata.columnType === "CODE_MAPPING") {
                        const safeKey = CSS.escape(element1.key);
                        const items = doc.querySelectorAll(`.${safeKey}`);
                        if (items.length > 0) {
                          items.forEach((item) => {
                            const formattedValue = Utils.roundToDecimalPlacesIfNumber(element1.value, 4);
                            item.innerText = formattedValue;
                          });
                          decodedHTML = doc.body.innerHTML;
                        }
                      }

                      if (element.keyValueType == "TYPE_2_TABLE" && element1.dataPeriod === 'BASE_YEAR' && element1.emissionFactors && element1.emissionFactors.length > 0) {
                        const rowNum = element1.metadata && element1.metadata.columnNumber
                          ? parseInt(element1.metadata.columnNumber.replace(/[A-Z]/, ''))
                          : null;

                        if (rowNum) {
                          element1.emissionFactors.forEach(ef => {
                            if (ef.ghg && ef.factor !== undefined && ef.factor !== null) {
                              const headerRow = doc.querySelector('thead tr');
                              if (headerRow) {
                                const headerCells = headerRow.querySelectorAll('th');
                                let colIndex = -1;
                                headerCells.forEach((headerCell, idx) => {
                                  if (headerCell.textContent.trim() === ef.ghg) {
                                    colIndex = idx;
                                  }
                                });

                                if (colIndex >= 0) {
                                  const dataRows = doc.querySelectorAll('tbody tr');
                                  const rowIndex = rowNum - 2;

                                  if (dataRows[rowIndex]) {
                                    const rowCells = dataRows[rowIndex].querySelectorAll('td');
                                    if (rowCells[colIndex]) {
                                      const factorValue = Utils.roundToDecimalPlacesIfNumber(ef.factor, 4);
                                      rowCells[colIndex].innerText = factorValue;
                                      decodedHTML = doc.body.innerHTML;
                                    }
                                  }
                                }
                              }
                            }
                          });
                        }
                      }

                    }
                  }
                  if (shouldPreserveSavedGHGTable(doc, element)) {
                    continue;
                  }
                  if (element.keyValueType == "TYPE_5_TABLE" ) {
                    const typeTableHolder = doc.getElementById(`typeTableHolder-${element.key}`);
                    if (!!typeTableHolder) {
                      const newHtml = pagetableTYPE_5Function(element, normalizeLanguageCode(reportClone.lang), {decimalPlaces: companyInfo.decimalPlaces});
                      typeTableHolder.outerHTML = newHtml;
                      decodedHTML = doc.body.innerHTML;
                    }
                  }
                  if (element.keyValueType == "TYPE_6_TABLE" ) {
                    try {
                      const typeTableHolder = doc.getElementById(`typeTableHolder-${element.key}`);
                      if (!!typeTableHolder) {
                        const newHtml = pagetableTYPE_6Function(element, normalizeLanguageCode(reportClone.lang), {decimalPlaces: companyInfo.decimalPlaces});
                        typeTableHolder.outerHTML = newHtml;
                        decodedHTML = doc.body.innerHTML;
                      } else {
                        const newHtml = pagetableTYPE_6Function(element, normalizeLanguageCode(reportClone.lang), {decimalPlaces: companyInfo.decimalPlaces});
                        const tempDiv = doc.createElement('div');
                        tempDiv.innerHTML = newHtml;
                        if (doc.body.lastChild) {
                          doc.body.insertBefore(tempDiv.firstChild, doc.body.lastChild.nextSibling);
                        } else {
                          doc.body.appendChild(tempDiv.firstChild);
                        }
                        decodedHTML = doc.body.innerHTML;
                      }
                    } catch (error) {
                      console.error('Error rendering TYPE_6_TABLE:', error, element);
                    }
                  }

                  if (element.keyValueType == "TYPE_7_TABLE") {
                    const newHtml = pagetableTYPE_7Function(element, normalizeLanguageCode(reportClone.lang), { decimalPlaces: companyInfo?.decimalPlaces });
                    const typeTableHolder = doc.getElementById(`typeTableHolder-${element.key}`);
                    if (!!typeTableHolder) {
                      typeTableHolder.outerHTML = newHtml;
                    } else {
                      const wrap = doc.createElement('div');
                      wrap.innerHTML = newHtml;
                      while (wrap.firstChild) {
                        doc.body.appendChild(wrap.firstChild);
                      }
                    }
                    decodedHTML = doc.body.innerHTML;
                  }
                }
              }
              page.html = decodedHTML;
              page.json = page.jsonContent ? JSON.parse(page.jsonContent) : null;
              page.section = typeof page.section === 'number'
                ? page.section
                : ReportSection[page.section];
              page.index = index;
              page.type = typeof page.type === 'number'
                ? page.type
                : ReportPageType[page.type];
            });
          });
          setReport(reportClone);
          setLoaderDisplay(false);
        }).catch(err => {
          toast.error("Error fetching template data", {
            position: toast.POSITION.TOP_RIGHT,
          });
          setLoaderDisplay(false);
        });
      }
    }).catch(error => {
      setLoaderDisplay(false);
      toast.error(error.title, {
        position: toast.POSITION.TOP_RIGHT,
      });
    });
  };

  const generateBRSRReportStructureFromTemplate = async () => {
    setLoaderDisplay(true);
    try {
      const reportRes = await ReportService.getBRSRReport(reportId);
      if (reportRes.status !== 200) {
        setLoaderDisplay(false);
        toast.error("Error fetching report", {
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }
      const existingReport = reportRes.data;
      const language = existingReport.lang || i18n.language;
      await ensureLanguageLoaded(i18n, language);
      const reportClone = {
        id: existingReport.id,
        name: existingReport.name,
        status: existingReport.status,
        sections: [],
        regulationId: existingReport.regulationId,
        companyId: existingReport.companyId,
        lang: existingReport.lang,
        period: existingReport.period,
        periodEnd: existingReport.periodEnd,
        basis: existingReport.basis
      };
      await generateBRSRReportStructure(reportClone, language, true);
    } catch (error) {
      setLoaderDisplay(false);
    }
  };

  const generateBRSRReportStructure = async (reportClone, language, shouldSave = false) => {
    try {
      await BRSRReportGenerator.generateBRSRReportStructure({
        reportClone,
        regulationId,
        yearFilter,
        t,
        setReport,
        setLoaderDisplay,
        shouldSave,
        saveBRSRReportStructure
      });
    } catch (error) {
      setLoaderDisplay(false);
      console.error('Error generating BRSR report structure:', error);
    }
  };

  const saveBRSRReportStructure = async (reportClone) => {
    try {
      // Prepare report data similar to createOrUpdateReport
      const reportData = JSON.parse(JSON.stringify(reportClone));
      reportData.sections?.forEach(section => {
        section?.pages?.forEach((page, index) => {
          let trimmedHTML = page.html?.replace(/>\s+</g, '><');
          page.htmlContent = trimmedHTML ? window.btoa(unescape(encodeURIComponent(trimmedHTML))) : null;
          page.jsonContent = page.json ? JSON.stringify(page.json) : null;
          delete page.html;
          delete page.json;
          delete page.index;
        });
        delete section.index;
      });

      reportData.regulationId = regulationId;
      reportData.companyId = companyId;
      reportData.status = ReportStatus.DRAFT;
      reportData.reportType = 'BRSR';

      const res = await ReportService.updateBRSRReport(reportClone.id, reportData);
      if (res.status === 200) {
        // Update URL to remove CREATE mode and reload report
        const newSearchParams = new URLSearchParams(window.location.search);
        newSearchParams.set('mode', 'EDIT');
        navigate(`/company_admin/reporting?${newSearchParams.toString()}`, { replace: true });
        // Fetch the updated report to ensure state is in sync
        fetchBRSRReport(reportClone.id);
      }
    } catch (error) {
    }
  };

  const fetchBRSRReport = (id) => {
    ReportService.getBRSRReport(id > 0 ? id : reportId).then(res => {
      if (res.status === 200) {
        const reportClone = res.data;
        reportClone.sections?.sort((a, b) => {
          return a.sortOrder - b.sortOrder;
        });
        if (isCoDraftMode) {
          reportClone.status = ReportStatus.DRAFT;
        }

        // Process sections and pages - decode HTML from backend
        reportClone.sections?.forEach((section, index) => {
          section.index = index;
          section.type = ReportSection[section.type];
          section.pages?.sort((a, b) => {
            return a.sortOrder - b.sortOrder;
          });
          section.pages?.forEach((page, index) => {
            const decodedHTML = page.htmlContent ? decodeURIComponent(escape(window.atob(page.htmlContent))) : null;
            page.html = decodedHTML ? decodedHTML : null;
            page.json = page.jsonContent ? JSON.parse(page.jsonContent) : null;
            page.section = ReportSection[page.section];
            page.index = index;
            page.type = ReportPageType[page.type];
          });
        });

        setReport(reportClone);
        setLoaderDisplay(false);
      }
    }).catch(error => {
      setLoaderDisplay(false);
    });
  };

  const createNewSectionInServer = async (sectionPayload, reportData, indexToInsert) => {
    try {
      setLoaderDisplay(true);
      delete sectionPayload.pages;
      const res = await ReportService.createReportSection(
        report.id || reportId,
        sectionPayload
      );
      if (res.status !== 200 && res.status !== 201) {
        throw new Error('Failed to add section');
      }
      const sectionId = res.data?.id;
      if (!sectionId) {
        throw new Error('Failed to add section');
      }
      const reportWithSectionId = {
        ...reportData,
        id: report.id || reportId,
        sections: reportData.sections.map((section, sIdx) =>
          sIdx === indexToInsert
            ? { ...section, id: sectionId }
            : section
        )
      };
      setReport(reportWithSectionId);
      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } catch (error) {
      toast.error(error?.title || error?.message || 'Failed to add section', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setLoaderDisplay(false);
    }
    return;
  };

  const deleteSectionFromServer = async (id) => {
    try {
      setLoaderDisplay(true);
      const res = await ReportService.deleteReportSection(
        report.id || reportId,
        id
      );
      if (!res.data) {
        throw new Error('Failed to delete section');
      }
      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } catch (error) {
      toast.error(error?.title || error?.message || 'Failed to delete section', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setLoaderDisplay(false);
    }
    return;
  };

  const createNewPageInServer = async (sectionIndex, pagePayload, reportData, indexToInsert) => {
    try {
      setLoaderDisplay(true);
      const res = await ReportService.createReportPage(
        report.id || reportId,
        reportData.sections[sectionIndex].id,
        pagePayload
      );
      if (res.status !== 200 && res.status !== 201) {
        throw new Error('Failed to add page');
      }
      const pageId = res.data?.id;
      if (!pageId) {
        throw new Error('Failed to add page');
      }
      const reportWithPageId = {
        ...reportData
      };
      reportWithPageId.sections[sectionIndex].pages[indexToInsert]['id'] = pageId;
      setReport(reportWithPageId);
      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } catch (error) {
      toast.error(error?.title || error?.message || 'Failed to add page', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setLoaderDisplay(false);
    }
    return;
  };

  const deletePageFromServer = async (sectionId, pageId) => {
    try {
      setLoaderDisplay(true);
      const res = await ReportService.deleteReportPage(
        report.id || reportId,
        sectionId,
        pageId
      );
      if (!res.data) {
        throw new Error('Failed to delete page');
      }
      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } catch (error) {
      toast.error(error?.title || error?.message || 'Failed to delete page', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setLoaderDisplay(false);
    }
    return;
  };

  const updateSectionInServer = async (sectionId, sectionPayload) => {
    try {
      setLoaderDisplay(true);
      delete sectionPayload.pages;
      const res = await ReportService.updateReportSection(
        report.id || reportId,
        sectionId,
        sectionPayload
      );
      if (!res.data) {
        throw new Error('Failed to update section');
      }
      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } catch (error) {
      toast.error(error?.title || error?.message || 'Failed to update section', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setLoaderDisplay(false);
    }
    return;
  };

  const updatePageInServer = async (pageId, pagePayload) => {
    try {
      setLoaderDisplay(true);
      const res = await ReportService.updateReportPage(
        report.id || reportId,
        pageId, pagePayload
      );
      if (!res.data) {
        throw new Error('Failed to update page');
      }
      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } catch (error) {
      toast.error(error?.title || error?.message || 'Failed to update page', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setLoaderDisplay(false);
    }
    return;
  };

  return (
    <div className="Reporting ">
      {checkStdTypeRepotingSidebar(stdType)}
      {
        (showPrompt || showConfirmation) && <ConfirmationModal
          modalShow={showPrompt || showConfirmation}
          messageText={t("LeaveEditorPrompt2.label")}
          callbackModal={(yes) => {
            handleUnsavedChangesConfirm(yes);
          }}
          confirmBtn={t("Save.label")}
          CancelBtn={t("DonotSave.label")}
        />
      }
    </div >);

}

export default withTranslation()(ReportingContainer);
